export const DEFAULT_BLOCK_ID = 'all'

import { CHARACTER_INDEX } from './character_index.generated.js'

const CODEPOINTS_BY_BLOCK = Object.entries(CHARACTER_INDEX).reduce((groups, [cp, meta]) => {
  const blockId = meta.blockId
  if (!groups[blockId]) groups[blockId] = []
  groups[blockId].push(Number(cp))
  return groups
}, {})

for (const blockId of Object.keys(CODEPOINTS_BY_BLOCK)) {
  CODEPOINTS_BY_BLOCK[blockId].sort((a, b) => a - b)
}

const ALL_CURATED_CODEPOINTS = Object.keys(CHARACTER_INDEX)
  .map(Number)
  .sort((a, b) => a - b)

// Visible category filters on /char. Keep in sync with CharactersHelper::CHAR_UNICODE_BLOCKS.
export const UNICODE_BLOCKS = [
  { id: 'all', label: 'All', icon: '◆', ranges: [] },
  { id: 'emojis', label: 'Emojis', icon: '😀', ranges: [] },
  { id: 'arrows', label: 'Arrows', icon: '→', ranges: [[0x2190, 0x21ff], [0x27f0, 0x27ff], [0x2900, 0x297f]] },
  { id: 'geometric', label: 'Shapes', icon: '●', ranges: [[0x25a0, 0x25ff]] },
  { id: 'lines', label: 'Lines', icon: '─', ranges: [[0x2500, 0x257f], [0x2580, 0x259f]] },
  { id: 'punctuation', label: 'Punctuation', icon: '…', ranges: [[0x2000, 0x206f], [0x2e00, 0x2e7f]] },
  { id: 'braille', label: 'Braille', icon: '⠿', ranges: [[0x2800, 0x28ff]] },
  { id: 'math', label: 'Math', icon: '∑', ranges: [[0x2200, 0x22ff]] },
  { id: 'currency', label: 'Currency', icon: '€', ranges: [[0x20a0, 0x20cf]] },
  {
    id: 'alphabet',
    label: 'Alphabet',
    icon: 'A',
    ranges: [
      [0x0020, 0x007e],
      [0x00a0, 0x00ff],
      [0x0180, 0x0186],
      [0x01dd, 0x01dd],
      [0x0250, 0x02af],
      [0x1d79, 0x1d9a],
      [0x218a, 0x218b],
    ],
  },
]

// Browsable under "All" only. No category chip.
const UNLISTED_BLOCK_RANGES = [
  { id: 'symbols', ranges: [[0x2600, 0x26ff], [0x2700, 0x27bf]] },
  { id: 'misc-symbols', ranges: [[0x2b00, 0x2bff]] },
]

function isArrowCharacter(meta) {
  if (!meta) return false

  return (meta.shapes || []).includes('arrow') || (meta.name || '').toUpperCase().includes('ARROW')
}

export function codepointsForBlock(block) {
  if (block.id === 'all') return ALL_CURATED_CODEPOINTS

  if (block.id === 'arrows') {
    const seen = new Set()
    const result = []

    for (const cp of ALL_CURATED_CODEPOINTS) {
      const meta = CHARACTER_INDEX[cp]
      if (meta?.blockId === 'arrows' || isArrowCharacter(meta)) {
        if (seen.has(cp)) continue
        seen.add(cp)
        result.push(cp)
      }
    }

    return result
  }

  return CODEPOINTS_BY_BLOCK[block.id] || []
}

export function blockForCodepoint(codepoint) {
  const indexed = CHARACTER_INDEX[codepoint]
  if (indexed?.blockId) return indexed.blockId

  for (const block of UNICODE_BLOCKS) {
    if (block.id === 'all' || block.id === 'emojis') continue
    for (const [start, end] of block.ranges) {
      if (codepoint >= start && codepoint <= end) return block.id
    }
  }

  for (const block of UNLISTED_BLOCK_RANGES) {
    for (const [start, end] of block.ranges) {
      if (codepoint >= start && codepoint <= end) return block.id
    }
  }

  return null
}

const EXTRA_CATEGORY_BLOCKS = {
  symbols: { id: 'symbols', label: 'Symbols', icon: '☀' },
  'misc-symbols': { id: 'misc-symbols', label: 'Misc symbols', icon: '⬆' },
}

export function categoryBlockById(blockId) {
  return UNICODE_BLOCKS.find((block) => block.id === blockId) || EXTRA_CATEGORY_BLOCKS[blockId] || null
}

export function categoriesForCodepoint(codepoint) {
  const ids = new Set()

  const indexed = CHARACTER_INDEX[codepoint]
  if (indexed?.blockId && indexed.blockId !== 'all') ids.add(indexed.blockId)
  if (isArrowCharacter(indexed)) ids.add('arrows')

  const derived = blockForCodepoint(codepoint)
  if (derived && derived !== 'all') ids.add(derived)

  return [...ids].map(categoryBlockById).filter(Boolean)
}

export function allCuratedCodepoints() {
  return ALL_CURATED_CODEPOINTS
}
